import { ECalendarMode, HandleDropResult } from '../types';
import { MINUTES_PER_HOUR } from './timeUtils';
import { calculateNewDate, getMinutesDifferenceByStep } from './index';
import { ISlot } from '../../organisms/bo_calendar_slot/types';
import { calculateTimeDifference, DateTimeFormats, TimeUnit } from '@bondsports/date-time';
import { getCombinedDateTimeString, MINUTES_PER_DAY } from '../../lib/timeUtils';
import dayjs from 'dayjs';

/**
 * Handles the drop event for daily calendar view.
 * @param slot {ISlot} - The slot being dragged.
 * @param delta {number} - The delta of the drag event.
 * @param size {number} - The size of the slot in the calendar view.
 * @param dndStepLength {number} - The step length for the drag and drop operation.
 * @return {HandleDropResult} - The result containing the new start and end dates, and the minutes difference.
 */
export function handleDailyDrop(slot: ISlot, delta: number, size: number, dndStepLength: number): HandleDropResult {
	const [startHour, startMinute]: string[] = slot.startTime.split(':');
	const [endHour, endMinute]: string[] = slot.endTime.split(':');

	const minutesDifference: number = getMinutesDifferenceByStep(
		slot.startDate,
		// Calculate the difference in minutes based on the drag distance
		// and the size of one hour in the calendar view
		Math.round(Number(delta) * (MINUTES_PER_HOUR / size)),
		dndStepLength
	);

	const startDate: string = calculateNewDate(
		dayjs
			.utc(dayjs.utc(slot.startDate).format('DD/MM/YYYY'), 'DD/MM/YYYY')
			.hour(Number(startHour))
			.minute(Number(startMinute))
			.format(),
		minutesDifference
	);
	const endDate: string = calculateNewDate(
		dayjs
			.utc(dayjs.utc(slot.endDate).format('DD/MM/YYYY'), 'DD/MM/YYYY')
			.hour(Number(endHour))
			.minute(Number(endMinute))
			.format(),
		minutesDifference
	);

	return {
		startDate,
		endDate,
		minutesDifference,
	};
}

/**
 * Handles the drop event for weekly calendar view.
 * @param slot {ISlot} - The slot being dragged.
 * @param date {string} - The date to which the slot is being dropped.
 * @return {HandleDropResult} - The result containing the new start and end dates.
 */
export function handleWeeklyDrop(slot: ISlot, date: string): HandleDropResult {
	const [startHour, startMinute]: string[] = slot.startTime.split(':');
	const [endHour, endMinute]: string[] = slot.endTime.split(':');

	const newDate: dayjs.Dayjs = dayjs
		.utc(date)
		.set('hour', Number(startHour))
		.set('minutes', Number(startMinute))
		.set('seconds', 0);

	const startDate: string = newDate.clone().format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);

	// calculating the end date based on the start date and the difference in days
	const differenceInDays: number = calculateTimeDifference(slot.startDate, slot.endDate, TimeUnit.DAY);

	// adding the difference in days to the new date
	const endDate: string = newDate
		.clone()
		.add(differenceInDays, TimeUnit.DAY)
		.set('hour', Number(endHour))
		.set('minute', Number(endMinute))
		.format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);

	return {
		startDate,
		endDate,
	};
}

/**
 * Handles the drop event for day X in the calendar.
 * @param slot {ISlot} - The slot being dragged.
 * @param originalDate {string} - The original date of the slot before dragging.
 * @param date {string} - The new date to which the slot is being dropped.
 * @param delta {number} - The delta of the drag event.
 * @param size {number} - The size of the slot in the calendar view.
 * @param dndStepLength {number} - The step length for the drag and drop operation.
 * @return {HandleDropResult} - The result containing the new start and end dates, and the minutes difference.
 */
export function handleDayXDrop(
	slot: ISlot,
	originalDate: string,
	date: string,
	delta: number,
	size: number,
	dndStepLength: number
): HandleDropResult {
	const [startHour, startMinute]: string[] = slot.startTime.split(':');

	const startDateTime: string = getCombinedDateTimeString(slot.startDate, slot.startTime);
	const endDateTime: string = getCombinedDateTimeString(slot.endDate, slot.endTime);

	const slotDuration: number = calculateTimeDifference(startDateTime, endDateTime, TimeUnit.MINUTE);

	const diffInDays: number = calculateTimeDifference(originalDate, date, TimeUnit.DAY);

	const minutesDifference: number = getMinutesDifferenceByStep(
		slot.startDate,
		// Calculate the difference in minutes based on the drag distance
		// and the size of one hour in the calendar view
		Math.round(Number(delta) * (MINUTES_PER_HOUR / size)),
		dndStepLength
	);

	const newDate: dayjs.Dayjs = dayjs
		.utc(slot.startDate)
		.set('hour', Number(startHour))
		.set('minutes', Number(startMinute))
		.set('seconds', 0)
		// adding the minutes difference to the new date
		// including the difference in days (as minutes) to the new date
		.add(minutesDifference + diffInDays * MINUTES_PER_DAY, TimeUnit.MINUTE);

	const startDate: string = newDate.clone().format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);
	const endDate: string = newDate.add(slotDuration, TimeUnit.MINUTE).format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);

	return {
		startDate,
		endDate,
		minutesDifference,
	};
}

/**
 * Checks if the calendar mode is daily.
 * @param mode {ECalendarMode} - The calendar mode to check.
 * @return {boolean} - Returns true if the mode is daily, otherwise false.
 */
export function isDailyMode(mode: ECalendarMode): boolean {
	return [ECalendarMode.DAILY, ECalendarMode.DAYX].includes(mode);
}
